import { useMemo } from "react";
import { useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";

import { useSelectorRoster } from "./use-selector-roster";
import type { QueryLike } from "@bfc-software/bfc-web-ui/lib/async-utils";

/**
 * A page-wide directory from a planner selector's ULID to a display name, for
 * the Activity timeline's "by" line (§11 Q4).
 *
 * Two reads, because the two sides speak different ids. `TaskActivityEvent`'s
 * `selector_id` is the planner `selector_profile.id` ULID (`task-planner.ts:923`);
 * `useSelectorRoster` (`use-selector-roster.ts`) is AAA's `/app_user/list`,
 * keyed by `user_id` (= `external_user_id`). No single existing read maps
 * ULID -> name, so this hook joins the two by hand, the same way `useTaskDetail`
 * joins `picks`/`history`.
 *
 * One page only (`limit: 100`, no `cursor` follow-up) - the engineer's call: at
 * most ~100 selectors, and not worth paginating for a "by" line. `orgId`/
 * `warehouseId` come from `useAssertedTenancyContext()`.
 */
export function useSelectorDirectory(): QueryLike<ReadonlyMap<string, string>> {
  const { orgId, warehouseId } = useAssertedTenancyContext();
  const client = useTaskPlannerApi();

  const selectors = client.useQuery(
    "get",
    "/{orgId}/selectors",
    {
      params: {
        path: { orgId },
        query: { warehouseExtId: warehouseId, limit: 100 },
      },
    },
    { select: (data) => data.selectors }, // ListSelectors -> SelectorList.selectors
  );

  // Cache-warm: SelectorFilter already mounts this on the same page (selector-filter.tsx:53).
  const roster = useSelectorRoster();

  const data = useMemo(() => {
    const selectorRows = selectors.data;
    const rosterRows = roster.data;
    if (!selectorRows || !rosterRows) return undefined;
    return new Map(
      selectorRows.map((s) => {
        const byExternalId = rosterRows.find(
          (o) => o.id === s.external_user_id,
        );
        return [s.id, byExternalId?.label ?? s.external_user_id]; // same fallback as use-selector-roster.ts:87
      }),
    );
  }, [selectors.data, roster.data]);

  return {
    isPending: selectors.isPending || roster.isPending,
    isError: selectors.isError || roster.isError,
    error: selectors.error ?? roster.error,
    data,
  };
}
